import { Links, Meta, Outlet, Scripts, ScrollRestoration, isRouteErrorResponse } from 'react-router';
import { MOCK_AUTH } from './authMode.js';
import usePageview from './analytics/usePageview.js';

// The empty <script data-boot-marker></script> services/web looks for and
// replaces with the real payload. An empty script rather than an HTML comment
// because it survives every HTML minifier unchanged and is trivially findable
// as one exact string; inert when nothing replaces it, which is the point.
// dangerouslySetInnerHTML with an empty string is how React is persuaded to
// emit an open/close pair rather than a self-closing tag.
const BOOT_MARKER = <script data-boot-marker dangerouslySetInnerHTML={{ __html: '' }} />;

// The document every frontend module renders inside, shared so the three
// module roots cannot drift apart (docs/frontend-modules-plan.md §12 names
// that as the split's first risk - fonts, the analytics tag, the theme, and
// the error page have to be identical across a hard navigation, and three
// copies of a <head> stay identical for about a week).
//
// Each module's own root.jsx supplies the two things that legitimately
// differ: its stylesheet URL (a module-relative `?url` import, which cannot
// be resolved from here) and its meta() defaults.

export function buildLinks(stylesheetHref) {
  return [
    { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap'
    },
    { rel: 'stylesheet', href: stylesheetHref }
  ];
}

export function Layout({ children }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {/* Website analytics - see app/_shared/analytics/ and
            services/analytics.js. One unassuming, first-party URL and
            nothing else: /w/b.js is GENERATED per environment and carries
            the umami website id, so the same prerendered HTML works against
            a self-hosted umami, against umami Cloud, or with analytics
            switched off entirely. Async, so it never blocks a paint, and
            everything downstream of it degrades to a no-op if it fails to
            load or is blocked.

            Omitted in MOCK_AUTH builds (e2e only): nothing serves /w there,
            so Vite's proxy answers with an HTML error page, and a browser
            parsing HTML as JavaScript raises a pageerror - which
            tests/e2e/upload-flow.test.mjs asserts on. */}
        {!MOCK_AUTH && <script async src="/w/b.js" />}
        {/* Where services/web writes the per-request boot payload, by
            rendering services/web/shells/boot.ejs over this marker - see
            @ui/boot.js for the contract that governs what may go in it.

            Left unreplaced, it is an empty script and the bundle simply has
            no payload: a bare `vite dev`, a prerendered page served straight
            off a static host, and a page whose request the server answered
            all take the same code path. Every reader has to cope with no
            payload anyway, so this makes that the common case rather than an
            edge one.

            Last in <head> on purpose: the payload is data for the bundle,
            and nothing above it should be waiting on it. */}
        {BOOT_MARKER}
      </head>
      <body className="bg-ink-950 text-paper font-sans antialiased selection:bg-brand/30 selection:text-paper min-h-screen">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// Nothing in any module has a build-time loader, so hydration is
// effectively instant - this exists to satisfy SPA mode's expectation of a
// HydrateFallback, not to cover a real loading window. `null` rather than a
// placeholder for the same reason the old AuthGate.jsx gave: body already
// carries the dark background, so there is nothing to flash to.
export function HydrateFallback() {
  return null;
}

// No provider to wrap the tree in - unlike Clerk, Better Auth's React
// client (authClient.js) has no context requirement at all; useSession() is
// a plain hook backed by nanostores, called directly wherever it is needed.
export function App() {
  // Pageviews for every module, with normalized URLs - see
  // analytics/usePageview.js for why the tracker's own auto-pageview is off.
  usePageview();
  return <Outlet />;
}

export function ErrorBoundary({ error }) {
  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Try reloading the page.';

  if (isRouteErrorResponse(error)) {
    title = error.status === 404 ? 'Page not found' : `Error ${error.status}`;
    message = error.status === 404 ? "The page you're looking for doesn't exist." : error.statusText || message;
  } else if (import.meta.env.DEV && error instanceof Error) {
    message = error.message;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display font-bold text-2xl text-paper mb-2">{title}</h1>
        <p className="text-ink-400 text-sm mb-6">{message}</p>
        {/* A plain anchor, not <Link> - "/" belongs to the web module and
            this boundary renders in all three. See ModuleLink.jsx. */}
        <a href="/" className="inline-flex items-center px-4 py-2 rounded-lg bg-brand text-ink-900 font-display font-semibold text-sm hover:bg-brand-600 transition-colors">
          Back to WriteUp
        </a>
      </div>
    </div>
  );
}
